import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../i18n/LanguageContext";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Ban, CheckCircle2, Download, Trash2, UserCog } from "lucide-react";

export default function SuperAdminUsers() {
  const { t } = useI18n();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ rows: [], total: 0, loading: true });
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 20;

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const data = await api.saListUsers({ q, role, limit, offset });
      setState({ rows: data.rows, total: data.total, loading: false });
    } catch (e) { toast.error(e.message); setState({ rows: [], total: 0, loading: false }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, role, offset]);

  const patch = async (uid, body, msg) => {
    try { await api.saPatchUser(uid, body); toast.success(msg || "✓"); load(); }
    catch (e) { toast.error(e.message); }
  };

  const impersonate = async (u) => {
    if (!window.confirm(t("superadmin.confirmImpersonate"))) return;
    try {
      await api.saImpersonate(u.user_id);
      await refresh();
      navigate("/dashboard");
      toast.success(`→ ${u.name || u.email}`);
    } catch (e) { toast.error(e.message); }
  };

  const del = async (u) => {
    if (!window.confirm(t("superadmin.confirmDeleteUser"))) return;
    try { await api.saDeleteUser(u.user_id); toast.success("✓"); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6" data-testid="sa-users-page">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#F72585]">{t("superadmin.title")}</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">{t("superadmin.users")}</h1>
        </div>
        <a href={api.saExportUrl("users")} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-2 rounded-full bg-white border border-black/10 px-4 py-2 text-sm font-bold hover:bg-black/5"
           data-testid="sa-export-users-link">
          <Download className="h-4 w-4" /> {t("superadmin.exportUsers")}
        </a>
      </header>

      <div className="flex flex-wrap gap-3 items-center">
        <Input value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }}
               placeholder={t("superadmin.searchUsers")}
               className="max-w-xs" data-testid="sa-users-search" />
        <select value={role} onChange={(e) => { setRole(e.target.value); setOffset(0); }}
                className="h-10 rounded-md border border-black/10 px-3 font-semibold bg-white text-sm"
                data-testid="sa-users-role-filter">
          <option value="">{`${t("superadmin.all")} ${t("superadmin.role").toLowerCase()}`}</option>
          <option value="student">student</option>
          <option value="admin">admin</option>
          <option value="super_admin">super_admin</option>
        </select>
        <div className="text-sm text-black/50">Total: <b>{state.total}</b></div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-black/5 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-black/5">
            <tr className="text-start">
              <Th>{t("superadmin.email")}</Th>
              <Th>{t("superadmin.role")}</Th>
              <Th>{t("superadmin.lastLogin")}</Th>
              <Th>{t("superadmin.createdAt")}</Th>
              <Th>{t("superadmin.actions")}</Th>
            </tr>
          </thead>
          <tbody>
            {state.loading && (
              <tr><td colSpan={5} className="p-6 text-center text-black/50">{t("superadmin.loading")}</td></tr>
            )}
            {!state.loading && state.rows.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-black/50">{t("superadmin.empty")}</td></tr>
            )}
            {state.rows.map((u) => (
              <tr key={u.user_id} className="border-t border-black/5 hover:bg-black/[0.02]"
                  data-testid={`sa-user-row-${u.user_id}`}>
                <Td>
                  <div className="font-bold">{u.name}</div>
                  <div className="text-xs text-black/50">{u.email}</div>
                </Td>
                <Td>
                  <Badge className={`text-[10px] ${
                    u.role === "super_admin" ? "bg-[#F72585]/20 text-[#B00060]" :
                    u.role === "admin" ? "bg-[#4361EE]/20 text-[#2A3EB1]" :
                    "bg-black/5 text-black/70"
                  }`}>{u.role}</Badge>
                  {u.is_banned && <Badge className="ms-1 text-[10px] bg-[#D90429]/20 text-[#D90429]">{t("superadmin.banned")}</Badge>}
                </Td>
                <Td className="text-xs text-black/60">{u.last_login_at?.slice(0, 16).replace("T", " ") || "—"}</Td>
                <Td className="text-xs text-black/60">{u.created_at?.slice(0, 10) || "—"}</Td>
                <Td>
                  <div className="flex flex-wrap gap-1.5">
                    {u.role !== "super_admin" && (
                      <>
                        <button
                          onClick={() => impersonate(u)}
                          className="rounded-full bg-[#1A1A1A] text-white text-xs px-3 py-1 font-bold hover:bg-black"
                          data-testid={`sa-impersonate-${u.user_id}`}
                        >
                          <UserCog className="h-3 w-3 inline me-1" /> {t("superadmin.impersonate")}
                        </button>
                        {u.role === "student" ? (
                          <button
                            onClick={() => patch(u.user_id, { role: "admin" }, t("superadmin.makeAdmin"))}
                            className="rounded-full bg-[#4361EE]/10 text-[#4361EE] text-xs px-3 py-1 font-bold hover:bg-[#4361EE]/20"
                            data-testid={`sa-makeadmin-${u.user_id}`}
                          >
                            {t("superadmin.makeAdmin")}
                          </button>
                        ) : (
                          <button
                            onClick={() => patch(u.user_id, { role: "student" }, t("superadmin.makeStudent"))}
                            className="rounded-full bg-black/5 text-black/70 text-xs px-3 py-1 font-bold hover:bg-black/10"
                            data-testid={`sa-makestudent-${u.user_id}`}
                          >
                            {t("superadmin.makeStudent")}
                          </button>
                        )}
                        {u.is_banned ? (
                          <button
                            onClick={() => window.confirm(t("superadmin.confirmUnban")) && patch(u.user_id, { is_banned: false }, "✓")}
                            className="rounded-full bg-[#4CC9F0]/20 text-[#0A5566] text-xs px-3 py-1 font-bold"
                            data-testid={`sa-unban-${u.user_id}`}
                          >
                            <CheckCircle2 className="h-3 w-3 inline me-1" /> {t("superadmin.unban")}
                          </button>
                        ) : (
                          <button
                            onClick={() => window.confirm(t("superadmin.confirmBan")) && patch(u.user_id, { is_banned: true }, "✓")}
                            className="rounded-full bg-[#D90429]/10 text-[#D90429] text-xs px-3 py-1 font-bold"
                            data-testid={`sa-ban-${u.user_id}`}
                          >
                            <Ban className="h-3 w-3 inline me-1" /> {t("superadmin.ban")}
                          </button>
                        )}
                        <button
                          onClick={() => del(u)}
                          className="rounded-full text-[#D90429] text-xs px-2 py-1 font-bold hover:bg-[#D90429]/10"
                          data-testid={`sa-delete-${u.user_id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm">
        <Button variant="outline" onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0}
                className="rounded-full font-bold" data-testid="sa-users-prev">
          ← Prev
        </Button>
        <div className="text-black/50">
          {offset + 1}–{Math.min(offset + limit, state.total)} / {state.total}
        </div>
        <Button variant="outline" onClick={() => setOffset(offset + limit)} disabled={offset + limit >= state.total}
                className="rounded-full font-bold" data-testid="sa-users-next">
          Next →
        </Button>
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-start px-4 py-3 text-xs font-black uppercase tracking-wider text-black/60">{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>;
}
